import { useEffect, useState } from "react";
import axios from "axios";
import { Youtube, Play, ExternalLink, Star, Calendar } from "lucide-react";
import BottomNav from "@/components/site/BottomNav";
import { MINISTRY } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNEL_VIDEOS_URL = `${MINISTRY.youtube}/videos`;
const CHANNEL_LIVE_URL = `${MINISTRY.youtube}/streams`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso.slice(0, 10); }
};

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/videos`).then((r) => setVideos(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const featured = videos.find((v) => v.is_featured) || videos[0];
  const gallery = videos.filter((v) => v !== featured);
  const thumb = (v) => v.thumbnail_url || (v.youtube_id ? `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg` : null);
  const ytWatch = (v) => v.youtube_id ? `https://www.youtube.com/watch?v=${v.youtube_id}` : MINISTRY.youtube;

  return (
    <div data-testid="videos-page">
      {/* HERO */}
      <section className="page-hero" style={{ backgroundColor: "var(--navy-900)" }}>
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse at center, rgba(217,184,100,0.4) 0%, transparent 60%)" }} />
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-600/20 border border-red-500/40 text-red-200 text-xs uppercase tracking-[0.3em] mb-5">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Live from YouTube · {MINISTRY.youtubeHandle}
          </div>
          <h1 className="font-display text-4xl lg:text-6xl text-cream leading-tight">Watch Our Videos</h1>
          <p className="mt-4 text-cream/80 text-lg max-w-2xl mx-auto">
            Christ-centered messages, devotional reflections, and Bible study videos to encourage your walk with Jesus.
          </p>
        </div>
      </section>

      {/* FEATURED PLAYER */}
      <section className="-mt-10 lg:-mt-16 relative z-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          {featured?.youtube_id ? (
            <div className="rounded-2xl overflow-hidden border-4 border-gold/50 shadow-2xl aspect-video bg-black" data-testid="featured-video-player">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${featured.youtube_id}?rel=0&modestbranding=1`}
                title={featured.title}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <a href={MINISTRY.youtube} target="_blank" rel="noreferrer" data-testid="featured-video-player"
              className="block rounded-2xl overflow-hidden border-4 border-gold/50 shadow-2xl aspect-video relative group"
              style={{ background: "linear-gradient(135deg, var(--navy-900) 0%, var(--navy-700) 50%, var(--gold-500) 100%)" }}>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-cream px-8 text-center">
                <div className="w-24 h-24 rounded-full bg-cream/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform mb-5">
                  <Play className="text-navy-deep ml-1" size={36} fill="currentColor" />
                </div>
                <p className="font-display text-3xl lg:text-4xl text-cream">
                  {loading ? "Loading latest videos..." : "Visit Our YouTube Channel"}
                </p>
                <p className="mt-3 text-gold-light text-sm tracking-[0.3em] uppercase">{MINISTRY.youtubeHandle}</p>
              </div>
            </a>
          )}
          {featured && (
            <div className="mt-5 text-center">
              <h2 className="font-display text-2xl lg:text-3xl text-navy-deep">{featured.title}</h2>
              <div className="mt-2 flex items-center justify-center gap-3 text-sm text-navy/60 flex-wrap">
                {featured.published_at && (
                  <span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-gold" /> {fmtDate(featured.published_at)}</span>
                )}
                {featured.source === "youtube" && <span className="px-2 py-0.5 rounded-full text-[0.65rem] uppercase tracking-wider bg-red-50 text-red-700 border border-red-200">Latest Upload</span>}
              </div>
              {featured.description && <p className="mt-3 text-navy/70 max-w-2xl mx-auto">{featured.description}</p>}
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={MINISTRY.youtube} target="_blank" rel="noreferrer" data-testid="videos-subscribe"
              className="btn-gold px-7 py-3 rounded-full inline-flex items-center justify-center gap-2">
              <Youtube size={18} /> Subscribe on YouTube
            </a>
            <a href={CHANNEL_LIVE_URL} target="_blank" rel="noreferrer" data-testid="videos-live"
              className="btn-outline-navy px-7 py-3 rounded-full inline-flex items-center justify-center gap-2">
              Watch Live Streams
            </a>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20 lg:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <div className="ornament mb-4">
              <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">From the Channel</span>
            </div>
            <h2 className="font-display text-3xl lg:text-4xl text-navy-deep">More Videos to Watch</h2>
            <p className="mt-3 text-sm text-navy/55">Updated automatically from our YouTube channel.</p>
          </div>

          {loading ? (
            <p className="text-center text-navy/55 py-12">Loading videos…</p>
          ) : gallery.length === 0 ? (
            <p className="text-center text-navy/55 py-12">More videos coming soon. Subscribe on YouTube to stay updated.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.map((v) => (
                <a key={v.id} href={ytWatch(v)} target="_blank" rel="noreferrer" data-testid={`video-card-${v.youtube_id || v.id}`}
                  className="lift bg-white rounded-2xl overflow-hidden border border-cream-200 hover:border-gold group">
                  <div className="relative aspect-video flex items-center justify-center overflow-hidden bg-navy-900">
                    {thumb(v) ? (
                      <img src={thumb(v)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full" style={{ background: "linear-gradient(135deg, var(--navy-800), var(--gold-500))" }} />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                        <Play className="text-navy-deep ml-0.5" size={22} fill="currentColor" />
                      </div>
                    </div>
                    {v.is_featured && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-gold text-navy-deep text-[0.65rem] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                        <Star size={10} fill="currentColor" /> Featured
                      </span>
                    )}
                    <span className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">YouTube</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg text-navy-deep leading-snug">{v.title}</h3>
                    <div className="mt-2 flex items-center gap-2 text-xs text-navy/55">
                      <Calendar size={12} className="text-gold" /> {fmtDate(v.published_at)}
                    </div>
                    {v.description && <p className="mt-2 text-sm text-navy/65 line-clamp-2">{v.description}</p>}
                    <div className="mt-4 inline-flex items-center gap-1.5 text-gold text-sm font-semibold">
                      Watch on YouTube <ExternalLink size={14} />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <a href={CHANNEL_VIDEOS_URL} target="_blank" rel="noreferrer" data-testid="videos-see-all"
              className="btn-primary px-7 py-3 rounded-full inline-flex items-center gap-2">
              See All Videos on YouTube <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </section>

      <BottomNav exclude={["/videos"]} />
    </div>
  );
}
